import { groupCubeRows, total, usd, type ExecutiveTransaction } from "../../data/executiveOverview";

const colors = ["#492750", "#3265AA", "#61BA93", "#E9702F", "#CA3500", "#776EA9", "#2B7FFF", "#894B00", "#B54708"];

export function SpendEnrichmentPanels({ rows }: { rows: ExecutiveTransaction[] }) {
  const totalSpend = total(rows, "Spend_Amount_USD") || 1;
  const supplierGroups = groupCubeRows(rows, "Parent_Supplier").slice(0, 9);
  const maxSupplierAmount = supplierGroups[0]?.amount || 1;
  const suppliers = supplierGroups.map((g, i) => ({
    name: g.label,
    revenue: usd(g.amount),
    sharePct: (g.amount / totalSpend) * 100,
    color: colors[i % colors.length],
    width: `${((g.amount / maxSupplierAmount) * 100).toFixed(0)}%`,
  }));
  const top3SharePct = suppliers.slice(0, 3).reduce((sum, s) => sum + s.sharePct, 0);

  const categoryGroups = groupCubeRows(rows, "Category_L2").slice(0, 5);
  const maxCategoryAmount = categoryGroups[0]?.amount || 1;
  const marketData = categoryGroups.map((g) => {
    const categoryRows = rows.filter((row) => row.Category_L2 === g.label);
    const unrealized = (categoryRows.reduce((sum, row) => sum + row.Market_Share_Unrealized_Pct, 0) / categoryRows.length) * 100;
    return { name: g.label, spend: (g.amount / maxCategoryAmount) * 100, unrealized };
  });
  const axisMax = Math.max(...marketData.flatMap((m) => [m.spend, m.unrealized]), 1);
  const topMarket = marketData.reduce<typeof marketData[number] | null>((top, m) => (!top || m.unrealized > top.unrealized ? m : top), null);

  return (
    <div className="spendbyproduct-row">
      <div className="spendbyproduct-col">
        <div className="supplier-card">
          <div className="supplier-title">
            SUPPLIER REVENUE DEPENDENCY
          </div>
          <div className="supplier-header">
            <span>SUPPLIERS</span>
            <span>REVENUE</span>
            <span>SHARE</span>
          </div>
          <div className="supplier-list">
            {suppliers.map((item) => (
              <div key={item.name} className="supplier-row">

                <div className="supplier-left">
                  <div className="supplier-name">{item.name}</div>

                  <div className="bar-wrapper">
                    <div
                      className="supplier-bar"
                      style={{
                        width: item.width,
                        background: item.color,
                      }}
                    />
                  </div>
                </div>

                <div className="supplier-revenue">{item.revenue}</div>
                <div className="supplier-share">{item.sharePct.toFixed(1)}%</div>

              </div>
            ))}
            {!suppliers.length && <div style={{ padding: "8px 0", color: "#888" }}>No spend matches the selected filters.</div>}
          </div>
          <div className="supplier-insight">
            <strong>Insight:</strong> Top 3 suppliers concentrate {top3SharePct.toFixed(0)}% of spend —{" "}
            {top3SharePct > 50 ? "concentrated base, consider diversification." : "reasonably distributed base."}
          </div>
        </div>
      </div>
      <div className="spendbyproduct-col">
        <div className="msa-card">
          <div className="msa-title">MARKET SHARE ANALYSIS</div>
          <div className="msa-sub">FISHBONE'S CATEGORY</div>
          <div className="msa-grid">
            <div className="msa-y">
              {marketData.map((item) => (
                <div key={item.name} className="msa-label" title={item.name}>
                  {item.name}
                </div>
              ))}
            </div>
            <div className="msa-chart-area">
              {marketData.map((item) => (
                <div key={item.name} className="msa-bars">
                  <div
                    className="msa-bar purple"
                    style={{ width: `${(item.spend / axisMax) * 100}%` }}
                  />
                  <div
                    className="msa-bar green"
                    style={{ width: `${(item.unrealized / axisMax) * 100}%` }}
                  />
                </div>
              ))}
              <div className="msa-grid-lines">
                <span className="msa-axis-line-vertical" />
                <span className="msa-axis-line-horizontal" />
              </div>
            </div>
          </div>
          <div className="msa-x">
            {[0, 1, 2, 3, 4].map((step) => (
              <span key={step}>{Math.round((axisMax * step) / 4)}%</span>
            ))}
          </div>
          <div className="msa-legend">
            <div><span className="msa-dot purple" /> Differential Spend</div>
            <div><span className="msa-dot green" /> Market Share Unrealized</div>
          </div>
          <div className="msa-insight">
            <strong>Insight:</strong>{" "}
            {topMarket
              ? `${topMarket.name} shows the highest unrealized market share (${topMarket.unrealized.toFixed(0)}%) among the top spend categories.`
              : "No data for the selected filters."}
          </div>
        </div>
      </div>
    </div>
  );
}
